import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  ArrowLeft,
  Calendar,
  LoaderCircle,
  MapPin,
  Save,
} from "lucide-react";
import { Button } from "../ui/button";
import { FieldError } from "../ui/field";
import { Input } from "../ui/input";
import ImageUploadGuidance from "../common/ImageUploadGuidance";
import InlineFormFileUpload, { IMAGE_TYPES } from "../file-upload/InlineFormFileUpload";
import { useAuthStore } from "../../context/AuthContext";
import { hasPermission } from "../../lib/utils/permissions";
import useToast from "../../hooks/useToast";
import apiClient from "../../lib/utils/network-client";

const EMPTY_FORM = {
  title: "",
  description: "",
  event_date: "",
  date_precision: "exact",
  event_year: "",
  place: "",
  thumbnail_image: null,
  gallery_images: [],
};

const EventFormPage = ({ mode }) => {
  const navigate = useNavigate();
  const { eventId } = useParams();
  const { user } = useAuthStore();
  const { showErrorToast, showSuccessToast } = useToast();
  const [form, setForm] = useState(EMPTY_FORM);
  const [formErrors, setFormErrors] = useState({});
  const [isPageLoading, setIsPageLoading] = useState(mode === "edit");
  const [isSaving, setIsSaving] = useState(false);
  const [thumbnailPreviewUrl, setThumbnailPreviewUrl] = useState("");
  const [existingGalleryImages, setExistingGalleryImages] = useState([]);

  const isEditMode = mode === "edit";
  const isAdmin = hasPermission(user, "events", isEditMode ? "edit" : "create");

  const pageTitle = useMemo(
    () => (isEditMode ? "Edit Event" : "Create Event"),
    [isEditMode],
  );

  useEffect(() => {
    if (!isEditMode || !isAdmin) {
      setIsPageLoading(false);
      return;
    }

    const fetchEvent = async () => {
      setIsPageLoading(true);
      try {
        const response = await apiClient.get(`/events/${eventId}`);
        const event = response.data?.data;

        if (!event) {
          showErrorToast("Event not found");
          navigate("/events");
          return;
        }

        setForm({
          title: event.title || "",
          description: event.description || "",
          event_date: event.event_date ? String(event.event_date).slice(0, 10) : "",
          date_precision: event.date_precision || "exact",
          event_year: event.event_date ? String(event.event_date).slice(0, 4) : "",
          place: event.place || "",
          thumbnail_image: null,
          gallery_images: [],
        });
        setThumbnailPreviewUrl(event.thumbnail_image_url || "");
        setExistingGalleryImages(event.gallery_images || []);
      } catch (err) {
        showErrorToast(err.response?.data?.message || "Failed to load event");
        navigate("/events");
      } finally {
        setIsPageLoading(false);
      }
    };

    fetchEvent();
  }, [eventId, isAdmin, isEditMode, navigate, showErrorToast]);

  const handleInputChange = (field, value) => {
    setForm((current) => ({
      ...current,
      [field]: value,
    }));
    setFormErrors((current) => ({
      ...current,
      [field]: "",
    }));
  };

  const validateForm = () => {
    const nextErrors = {};

    if (!form.title.trim()) nextErrors.title = "Event title is required.";
    if (!form.description.trim()) nextErrors.description = "Event description is required.";
    if (form.date_precision === "year") {
      const year = Number.parseInt(form.event_year, 10);
      if (!Number.isInteger(year) || year < 1900 || year > 2100) {
        nextErrors.event_year = "Enter a valid event year.";
      }
    } else if (!form.event_date) {
      nextErrors.event_date = "Event date is required.";
    }
    if (!form.place.trim()) nextErrors.place = "Event place is required.";
    if (!isEditMode && !form.thumbnail_image) {
      nextErrors.thumbnail_image = "Thumbnail image is required.";
    }
    setFormErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const createFormData = () => {
    const payload = new FormData();
    payload.append("title", form.title.trim());
    payload.append("description", form.description.trim());
    payload.append(
      "event_date",
      form.date_precision === "year"
        ? `${form.event_year}-01-01`
        : form.event_date,
    );
    payload.append("date_precision", form.date_precision);
    payload.append("place", form.place.trim());

    if (form.thumbnail_image) {
      payload.append("thumbnail_image", form.thumbnail_image);
    }

    form.gallery_images.forEach((file) => {
      payload.append("gallery_images", file);
    });

    return payload;
  };

  const handleSave = async (event) => {
    event.preventDefault();

    if (!validateForm()) {
      return;
    }

    setIsSaving(true);
    try {
      const payload = createFormData();

      if (isEditMode) {
        await apiClient.put(`/events/${eventId}`, payload, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        showSuccessToast("Event updated successfully");
      } else {
        await apiClient.post("/events", payload, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        showSuccessToast("Event created successfully");
      }

      navigate("/events");
    } catch (err) {
      showErrorToast(err.response?.data?.message || "Unable to save event");
    } finally {
      setIsSaving(false);
    }
  };

  if (!isAdmin) {
    return (
      <div className="rounded-lg border border-white/60 bg-white/80 p-8 shadow-sm">
        <h1 className="text-2xl font-bold text-slate-900">{pageTitle}</h1>
        <p className="mt-2 text-sm text-slate-500">
          Event management is only available for admin users.
        </p>
      </div>
    );
  }

  if (isPageLoading) {
    return (
      <div className="rounded-lg border border-white/60 bg-white/80 p-8 shadow-sm">
        <div className="flex items-center gap-3 text-sm font-medium text-slate-500">
          <LoaderCircle className="h-4 w-4 animate-spin" />
          Loading event editor...
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-[1600px] space-y-6">
      <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
        <div>
          <Button
            type="button"
            variant="ghost"
            className="-ml-3 mb-2 rounded-lg px-3 text-slate-500"
            onClick={() => navigate("/events")}
          >
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back to Events
          </Button>
          <h1 className="text-3xl font-bold tracking-tight text-slate-900">
            {pageTitle}
          </h1>
          <p className="mt-1 text-sm font-medium text-slate-500">
            {isEditMode
              ? "Update event information, thumbnail, and gallery images."
              : "Create an event with title, description, images, date, and place."}
          </p>
        </div>
      </div>

      <form
        onSubmit={handleSave}
        className="rounded-lg border border-white/60 bg-white/80 p-6 shadow-sm backdrop-blur-xl"
      >
        <div className="grid gap-6 lg:grid-cols-[1.1fr_0.9fr]">
          <div className="space-y-4">
            <div className="space-y-2">
              <label className="text-sm font-semibold text-slate-700">Title</label>
              <Input
                value={form.title}
                onChange={(event) => handleInputChange("title", event.target.value)}
                placeholder="Annual health awareness camp"
                className={`rounded-lg ${formErrors.title ? "border-red-300 focus-visible:ring-red-500" : ""}`}
              />
              <FieldError>{formErrors.title}</FieldError>
            </div>

            <div className="space-y-2">
              <label className="text-sm font-semibold text-slate-700">Description</label>
              <textarea
                value={form.description}
                onChange={(event) =>
                  handleInputChange("description", event.target.value)
                }
                rows={8}
                placeholder="Write event details here..."
                className={`min-h-[180px] w-full rounded-lg border bg-white px-4 py-3 text-sm text-slate-700 outline-none ${
                  formErrors.description
                    ? "border-red-300 focus:ring-2 focus:ring-red-500"
                    : "border-slate-200 focus:ring-2 focus:ring-primary/30"
                }`}
              />
              <FieldError>{formErrors.description}</FieldError>
            </div>

            <div className="grid gap-4 md:grid-cols-2">
              <div className="space-y-2">
                <label className="text-sm font-semibold text-slate-700">
                  Date Display
                </label>
                <select
                  value={form.date_precision}
                  onChange={(event) =>
                    handleInputChange("date_precision", event.target.value)
                  }
                  className="h-10 w-full rounded-lg border border-slate-200 bg-white px-3 text-sm text-slate-700 outline-none focus:ring-2 focus:ring-primary/30"
                >
                  <option value="exact">Exact date</option>
                  <option value="year">Year only</option>
                </select>
              </div>

              <div className="space-y-2">
                <label className="text-sm font-semibold text-slate-700">
                  {form.date_precision === "year" ? "Year" : "Date"}
                </label>
                <div className="relative">
                  <Calendar className="pointer-events-none absolute left-3 top-3.5 h-4 w-4 text-slate-400" />
                  <Input
                    type={form.date_precision === "year" ? "number" : "date"}
                    min={form.date_precision === "year" ? "1900" : undefined}
                    max={form.date_precision === "year" ? "2100" : undefined}
                    value={
                      form.date_precision === "year"
                        ? form.event_year
                        : form.event_date
                    }
                    onChange={(event) =>
                      handleInputChange(
                        form.date_precision === "year" ? "event_year" : "event_date",
                        event.target.value,
                      )
                    }
                    className={`rounded-lg pl-10 ${
                      formErrors.event_date || formErrors.event_year
                        ? "border-red-300 focus-visible:ring-red-500"
                        : ""
                    }`}
                  />
                </div>
                <FieldError>
                  {formErrors.event_date || formErrors.event_year}
                </FieldError>
              </div>

              <div className="space-y-2">
                <label className="text-sm font-semibold text-slate-700">Place</label>
                <div className="relative">
                  <MapPin className="pointer-events-none absolute left-3 top-3.5 h-4 w-4 text-slate-400" />
                  <Input
                    value={form.place}
                    onChange={(event) => handleInputChange("place", event.target.value)}
                    placeholder="GCS Hospital Auditorium"
                    className={`rounded-lg pl-10 ${formErrors.place ? "border-red-300 focus-visible:ring-red-500" : ""}`}
                  />
                </div>
                <FieldError>{formErrors.place}</FieldError>
              </div>

            </div>

            <div className="space-y-2">
              <InlineFormFileUpload
                label="Thumbnail Image"
                value={form.thumbnail_image}
                onChange={(file) => handleInputChange("thumbnail_image", file)}
                existingFiles={thumbnailPreviewUrl ? [thumbnailPreviewUrl] : []}
                accept={IMAGE_TYPES}
                maxSizeBytes={5 * 1024 * 1024}
                required={!isEditMode}
                error={formErrors.thumbnail_image}
                helperText="JPG, PNG, GIF or WEBP — maximum 5 MB"
              />
              <ImageUploadGuidance
                requirementKey="eventThumbnail"
                file={form.thumbnail_image}
                src={thumbnailPreviewUrl}
              />
            </div>
          </div>

          <div className="space-y-4">
            <div className="space-y-2">
              <InlineFormFileUpload
                label="Gallery Images"
                value={form.gallery_images}
                onChange={(files) => handleInputChange("gallery_images", files)}
                existingFiles={existingGalleryImages}
                accept={IMAGE_TYPES}
                maxSizeBytes={5 * 1024 * 1024}
                maxFiles={20}
                countExistingTowardsMax={false}
                error={formErrors.gallery_images}
                helperText="Select up to 20 JPG, PNG, GIF or WEBP images — maximum 5 MB each. In edit mode, new files replace the current gallery after save."
              />
              <ImageUploadGuidance
                requirementKey="eventGallery"
                files={form.gallery_images}
                sources={existingGalleryImages}
              />
            </div>

          </div>
        </div>

        <div className="mt-6 flex flex-col gap-3 border-t border-slate-100 pt-5 sm:flex-row sm:justify-end">
          <Button
            type="button"
            variant="outline"
            className="rounded-lg"
            onClick={() => navigate("/events")}
          >
            Cancel
          </Button>
          <Button type="submit" className="rounded-lg" disabled={isSaving}>
            {isSaving ? (
              <LoaderCircle className="mr-2 h-4 w-4 animate-spin" />
            ) : (
              <Save className="mr-2 h-4 w-4" />
            )}
            {isSaving ? "Saving..." : isEditMode ? "Update Event" : "Create Event"}
          </Button>
        </div>
      </form>
    </div>
  );
};

export default EventFormPage;
